/**
 * RegistrationForm — autenticazione Phase 5 (IMPL-P5-009).
 *
 * UC-005 signup form. Wires `react-hook-form` + `zodResolver(signupSchema)`
 * from `@app/shared` so validation messages stay in lockstep with the API
 * boundary. Submits to `/api/auth/custom/signup` via `authClient.signup()`.
 *
 * Field order (per Phase 5 directives):
 *   1. UserTypeRadioGroup (tipologia)
 *   2. email
 *   3. password (with PasswordStrengthMeter)
 *   4. confirmPassword
 *   5. ConsentCheckboxGroup (TOS + Privacy required, Marketing optional)
 *   6. Submit button
 *
 * Server-error handling:
 *   - 409 EMAIL_ALREADY_REGISTERED → inline `<Alert>` with links to login
 *     and forgot-password (deferred Phase 6 route is still navigable).
 *   - Any other failure → generic neutral Italian copy from
 *     `serverErrorToItalian` so we never leak server detail.
 */
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { signupSchema, type SignupInput } from '@app/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { authClient } from '../lib/auth-client';
import { isAuthClientError } from '../lib/auth-client';
import { serverErrorToItalian } from '../lib/errors';
import { CONSENT_VERSION } from '../lib/legal-copy';
import { PasswordField } from './PasswordField';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import { UserTypeRadioGroup } from './UserTypeRadioGroup';
import { ConsentCheckboxGroup } from './ConsentCheckboxGroup';

interface RegistrationFormProps {
  onSuccess: (email: string) => void;
}

interface ServerError {
  status: number;
  code: string;
  message: string;
}

export function RegistrationForm({ onSuccess }: RegistrationFormProps) {
  const [serverError, setServerError] = useState<ServerError | null>(null);

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      tipologia: undefined as unknown as SignupInput['tipologia'],
      consents: {
        tos: false as unknown as true,
        privacy: false as unknown as true,
        marketing: false,
        version: CONSENT_VERSION,
      },
    },
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const passwordValue = watch('password') ?? '';

  async function onSubmit(values: SignupInput) {
    setServerError(null);
    try {
      await authClient.signup(values);
      onSuccess(values.email);
    } catch (err: unknown) {
      if (isAuthClientError(err)) {
        setServerError({ status: err.status, code: err.code, message: err.message });
      } else {
        setServerError({
          status: 0,
          code: 'UNKNOWN',
          message: serverErrorToItalian(undefined),
        });
      }
    }
  }

  const showAlreadyRegistered =
    serverError?.status === 409 && serverError.code === 'EMAIL_ALREADY_REGISTERED';

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      aria-describedby={serverError ? 'signup-server-error' : undefined}
    >
      {/* Tipologia */}
      <div className="space-y-2">
        <Label>Tipologia utente</Label>
        <Controller
          control={control}
          name="tipologia"
          render={({ field, fieldState }) => (
            <UserTypeRadioGroup
              value={field.value as 'PRIVATO' | 'AGENTE' | 'COMMERCIALISTA' | undefined}
              onValueChange={field.onChange}
              name={field.name}
              invalid={!!fieldState.error}
              describedBy={fieldState.error ? 'tipologia-error' : undefined}
            />
          )}
        />
        {errors.tipologia ? (
          <p id="tipologia-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.tipologia.message}
          </p>
        ) : null}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="nome@esempio.it"
          aria-invalid={!!errors.email || undefined}
          aria-describedby={errors.email ? 'signup-email-error' : undefined}
          {...register('email')}
        />
        {errors.email ? (
          <p id="signup-email-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      {/* Password + strength meter */}
      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <PasswordField
          id="signup-password"
          autoComplete="new-password"
          aria-describedby="signup-password-strength signup-password-error"
          invalid={!!errors.password}
          {...register('password')}
        />
        <PasswordStrengthMeter id="signup-password-strength" password={passwordValue} />
        {errors.password ? (
          <p id="signup-password-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.password.message}
          </p>
        ) : null}
      </div>

      {/* Confirm password */}
      <div className="space-y-2">
        <Label htmlFor="signup-confirm">Conferma password</Label>
        <PasswordField
          id="signup-confirm"
          autoComplete="new-password"
          aria-describedby={errors.confirmPassword ? 'signup-confirm-error' : undefined}
          invalid={!!errors.confirmPassword}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword ? (
          <p id="signup-confirm-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.confirmPassword.message}
          </p>
        ) : null}
      </div>

      {/* Consents */}
      <Controller
        control={control}
        name="consents"
        render={({ field }) => (
          <ConsentCheckboxGroup
            value={{
              tos: !!field.value?.tos,
              privacy: !!field.value?.privacy,
              marketing: !!field.value?.marketing,
            }}
            onChange={(next) =>
              field.onChange({
                ...field.value,
                tos: next.tos,
                privacy: next.privacy,
                marketing: next.marketing,
                version: CONSENT_VERSION,
              })
            }
            errors={{
              tos: errors.consents?.tos?.message,
              privacy: errors.consents?.privacy?.message,
            }}
          />
        )}
      />

      {/* Server-side errors */}
      {showAlreadyRegistered ? (
        <Alert variant="destructive" id="signup-server-error">
          <AlertTitle>Email già registrata</AlertTitle>
          <AlertDescription>
            Questa email risulta già registrata. Puoi{' '}
            <Link to="/accedi" className="underline underline-offset-4 font-medium">
              accedere
            </Link>{' '}
            oppure{' '}
            <Link to="/password-dimenticata" className="underline underline-offset-4 font-medium">
              reimpostare la password
            </Link>
            .
          </AlertDescription>
        </Alert>
      ) : serverError ? (
        <Alert variant="destructive" id="signup-server-error">
          <AlertDescription>{serverErrorToItalian(serverError.code)}</AlertDescription>
        </Alert>
      ) : null}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Creazione account…' : 'Crea account'}
      </Button>
    </form>
  );
}
